import {
  Component,
  OnDestroy,
  OnInit,
  OnChanges,
  AfterViewInit,
  Input,
  SimpleChanges,
} from "@angular/core";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { OESettingsService } from "../services/basic/oe-settings.service";
import { OEUtilityService } from "../services/basic/oe-utility.service";
import { OEApiService } from "../services/oe-api.service";
import { OENDisplayState } from "../data/enums/oe-display-state.enum";
import { Subscription } from "rxjs";
import { OENCommand } from "../data/enums/oe-command.enum";
import { OECoordinatorService } from "../services/oe-coordinator.service";
import { OECommandEventModel } from "../data/models/oe-command-event.model";
import { OENEntryType } from "../data/enums/oe-entry-type.enum";
import { ATDLComparatorService } from "../services/atdl/atdl-comparator.service";
import { ATDLParserService } from "../services/atdl/atdl-parser.service";
import { ATDLProcessorService } from "../services/atdl/atdl-processor.service";
import { OENExtendedLoggingType } from "../data/enums/oe-extended-logging-type.enum";

@Component({
  selector: "oe-panel-debug",
  templateUrl: "./html/oe-panel-debug.html",
  standalone: false,
})
export class OEDebugPanelComponent implements OnDestroy, OnInit, OnChanges, AfterViewInit {
  @Input() refId: string;
  @Input() rootRefId: string; // refId of the root order
  @Input() refIds: Array<string> = new Array<string>(); // refIds for all strategy legs
  @Input() entryType: OENEntryType; // order entry type

  /***************************************/
  /* DATA */
  public refIdsDisplayList: string = "";

  public control: any = {
    extendedLogging: false,
    dumpModelsLogging: false,
    dumpModelsLoggingDisabled: true,

    extendedLogging_rate: false,
    dumpModelsLogging_rate: false,
    dumpModelsLoggingDisabled_rate: true,

    activeDebugTabIndex: 0,
    state: {
      actions_state: OENDisplayState.SHOW_ORDER_ENTRY,
      actions_state_name: "SHOW_ORDER_ENTRY",
      actions_state_next_name: "SHOW_PROCESSING",
    },
    lock_controls: false, // for all
    algo_control_validation_active: true,
    strictClientDatasetMode: false,
    oboUser: null,
  };

  private coordinatorSubscription: Subscription;

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    private translate: TranslateService,
    private settings: OESettingsService,
    private utility: OEUtilityService,
    private api: OEApiService,
    private coordinator: OECoordinatorService,
    private atdlProcessor: ATDLProcessorService,
  ) {
    this.initialize();
  }

  /**********************************************************************/
  /* INITIALIZATION */

  protected initialize(): void {
    this.subscribeToCoordinator();
    this.updateExtendedWorkerLogSate();
    this.updateClientDatasetMode();
  }

  private subscribeToCoordinator(): void {
    this.coordinatorSubscription = this.coordinator.debugPanelCmd$.subscribe(
      ($event: OECommandEventModel) => {
        if ($event.ref === this.refId || $event.ref === this.rootRefId) {
          switch ($event.cmd) {
            case OENCommand.EXTENDED_LOG_UPDATE:
              if ($event.data != null && $event.data.type === OENExtendedLoggingType.RATE) {
                if (
                  typeof $event.data.extLog === "boolean" &&
                  typeof $event.data.mdLog === "boolean"
                ) {
                  this.control.extendedLogging_rate = $event.data.extLog;
                  this.updateRateDumpModelLoggingValue($event.data.mdLog);
                } else {
                  this.updateRateExtendedWorkerLogSate();
                }
              } else {
                if (
                  typeof $event.data.extLog === "boolean" &&
                  typeof $event.data.mdLog === "boolean"
                ) {
                  this.control.extendedLogging = $event.data.extLog;
                  this.updateDumpModelLoggingValue($event.data.mdLog);
                } else {
                  this.updateExtendedWorkerLogSate();
                }
              }
              break;
            case OENCommand.STATE_ORDER_ENTRY:
            case OENCommand.STATE_ORDER_IN_MODIFICATION:
            case OENCommand.STATE_ORDER_UNLOCKED:
            case OENCommand.STATE_ORDER_PROCESSING:
            case OENCommand.STATE_ORDER_ENTRY_TOLERANCE_ERROR:
            case OENCommand.STATE_ORDER_ENTRY_TOLERANCE_WARN:
            case OENCommand.STATE_ORDER_SUCCESS:
            case OENCommand.STATE_ORDER_REJECTED:
            case OENCommand.STATE_ORDER_REJECTED_HCO_ERROR:
            case OENCommand.STATE_ORDER_INT_ERROR:
            case OENCommand.STATE_ORDER_ACCEPTANCE_ALLOWED:
            case OENCommand.STATE_ORDER_ACCEPTANCE_CALL:
            case OENCommand.STATE_ORDER_ACCEPTANCE_EXTERNAL:
            case OENCommand.STATE_ORDER_MODIFY_OUT_ACCEPTANCE:
            case OENCommand.STATE_ORDER_EXT_MODIFY_OUT_ACCEPTANCE:
            case OENCommand.STATE_ORDER_APPROVAL_ALLOWED:
            case OENCommand.STATE_ORDER_APPROVAL_INTERNAL:
            case OENCommand.STATE_ORDER_APPROVAL_EXTERNAL:
            case OENCommand.STATE_ORDER_AMEND_MARKUP:
            case OENCommand.STATE_ORDER_EXPIRY_PENDING:
              this.control.state.displayState = this.api.transformCmdToDisplayState($event.cmd);
              this.updateRefIdsDisplay();
              break;
            case OENCommand.LOCK_ALL:
              this.control.lock_controls = true;
              break;
            case OENCommand.UNLOCK_ALL:
              this.control.lock_controls = false;
              break;
            case OENCommand.UNBULK:
            case OENCommand.BULKED:
            case OENCommand.BULKING: {
              break;
            }
            default:
              logger.out.error(
                `ERROR: oeDebugPanel.subscribeToCoordinator(): Command not implemented! command= (${
                  $event.cmd
                }) [${$event.cmd != null ? OENCommand[$event.cmd] : "NULL"}]`,
              );
              break;
          }
        }
      },
    );
  }

  /**********************************************************************/
  /* LIFE CYCLE HOOKS */

  ngOnInit() {}

  ngOnDestroy() {
    this.coordinatorSubscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Dev-test - to be deleted
    /* 		
		for (let propName in changes) {
			if (this.hasOwnProperty(propName)) {
				let change = changes[propName];
			}
		} */
    // for debug
    this.updateRefIdsDisplay();
  }

  ngAfterViewInit() {
    this.updateRefIdsDisplay();
  }

  /**********************************************************************/
  /*  DEV TESTING API = debug menu actions */

  public onDebugTabActivate($event): void {}

  public lockOrderEntry(): void {
    this.api.actionLockAll(this.rootRefId);
    if (this.rootRefId !== this.refId) this.api.actionLockAll(this.refId);
  }
  public unlockOrderEntry(): void {
    this.api.actionUnlockAll(this.rootRefId);
    if (this.rootRefId !== this.refId) this.api.actionUnlockAll(this.refId);
  }

  public changeButtonConfig(): void {
    this.control.state.actions_state = ++this.control.state.actions_state % 15;
    this.control.state.actions_state_name = OENDisplayState[this.control.state.actions_state];
    if (this.control.state.actions_state !== 14) {
      this.control.state.actions_state_next_name =
        OENDisplayState[this.control.state.actions_state + 1];
    } else {
      this.control.state.actions_state_next_name = OENDisplayState[0];
    }
    this.api.testActionOrderStateDisplay(this.rootRefId, this.control.state.actions_state);
  }

  public toggleClientDatasetMode(): void {
    this.control.strictClientDatasetMode = !this.control.strictClientDatasetMode;
    this.api.overwriteClientDatasetMode(this.control.strictClientDatasetMode);
  }

  public updateClientDatasetMode(): void {
    let value: boolean = this.api.isClientDatasetModeStrict();
    if (typeof value === "boolean") {
      this.control.strictClientDatasetMode = value;
    }
  }

  /**********************************************************************/
  /*  ALGO */
  public parseATDLXML(): void {
    this.atdlProcessor.reinitialize();
  }

  public validateAlgoPanel(): void {
    this.coordinator.testValidateAlgoPanel(this.refId);
  }
  public validateAlgoParams(): void {
    this.api.testValidateAlgoParams(this.refId);
  }

  public deactivateControlValidation(): void {
    this.control.algo_control_validation_active = false;
    this.coordinator.testAlgoControlValidationState(this.refId, false);
  }

  public activateControlValidation(): void {
    this.control.algo_control_validation_active = true;
    this.coordinator.testAlgoControlValidationState(this.refId, true);
  }

  /**********************************************************************/
  /*  DEBUG Logging */

  // interface - generic
  public onDebugExtendedLoggingChange() {
    this.api.setExtendedWorkerLoggingState(this.control.extendedLogging);
    this.updateDumpModelLoggingValue(this.api.getDumpModelLoggingState());
  }

  public onDebugOBOUserChange() {
    if (this.control.oboUser != null && this.control.oboUser.trim().length > 0) {
      this.api.updateOBOUser(this.rootRefId, this.control.oboUser);
    }
  }

  public onDebugDumpModelLoggingChange() {
    this.updateDumpModelLoggingValue(
      this.api.setDumpModelLoggingState(this.control.dumpModelsLogging),
    );
  }
  // internal -generic
  public updateExtendedWorkerLogSate() {
    this.control.extendedLogging = this.api.getExtendedLoggingState();
    this.updateDumpModelLoggingValue(this.api.getDumpModelLoggingState());
  }

  private updateDumpModelLoggingValue(value: boolean): void {
    this.control.dumpModelsLoggingDisabled = false;
    setTimeout(() => {
      this.control.dumpModelsLogging = value;
      this.control.dumpModelsLoggingDisabled = !this.control.extendedLogging;
    }, 100);
  }
  // interface - rate
  public onDebugRateExtendedLoggingChange() {
    this.api.setRateExtendedWorkerLoggingState(this.control.extendedLogging_rate);
    this.updateRateDumpModelLoggingValue(this.api.getRateDumpModelLoggingState());
  }

  public onDebugRateDumpModelLoggingChange() {
    this.updateRateDumpModelLoggingValue(
      this.api.setRateDumpModelLoggingState(this.control.dumpModelsLogging_rate),
    );
  }

  // internal -rate
  public updateRateExtendedWorkerLogSate() {
    this.control.extendedLogging_rate = this.api.getRateExtendedWorkerLoggingState();
    this.updateRateDumpModelLoggingValue(this.api.getRateDumpModelLoggingState());
  }

  private updateRateDumpModelLoggingValue(value: boolean): void {
    this.control.dumpModelsLoggingDisabled_rate = false;
    setTimeout(() => {
      this.control.dumpModelsLogging_rate = value;
      this.control.dumpModelsLoggingDisabled_rate = !this.control.extendedLogging_rate;
    }, 100);
  }

  // refIds
  private updateRefIdsDisplay(): void {
    try {
      let result = "";
      this.refIds.forEach((refId: string) => {
        let orderId = this.api.getOrderIdForRefId(refId);
        let entryTxt = orderId != null ? refId + " -> ID: " + orderId : refId + " -> ID: N/A";
        result = result.length < 2 ? entryTxt : result + "<br/>" + entryTxt;
      });
      //this.refIdsDisplayList = this.refIds.join('<br/>');
      this.refIdsDisplayList = result;
    } catch (ex) {
      logger.out.error(`ERROR: debugPanel.updateRefIdsDisplay(): Exception: %O`, ex);
    }
  }
}
